"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import Popover from "../Popover/index.js";
import MenuItem from "../MenuItem/index.js";
import Icon from "../Icon/index.js";
import { useFlyoutContext } from "../Flyout/index.js";
import IconChevronRight from "../../icons/ChevronRight.js";
import useHotkeys from "../../hooks/useHotkeys.js";
import useRTL from "../../hooks/useRTL.js";
import { classNames } from "../../utilities/props.js";
import * as keys from "../../constants/keys.js";
import s from "./DropdownMenu.module.css";
const DropdownMenuSubContext = React.createContext(null);
const DropdownMenuSubTriggerContext = React.createContext(false);
const DropdownMenu = (props) => {
    const { children, position = "bottom-start", triggerType = "click", trapFocusMode = "action-menu", ...popoverProps } = props;
    return (_jsx(Popover, { ...popoverProps, position: position, padding: 0, trapFocusMode: trapFocusMode, triggerType: triggerType, children: children }));
};
const DropdownMenuContent = (props) => {
    const { children, attributes, className } = props;
    const { flyout } = useFlyoutContext();
    const subMenuInstance = React.useContext(DropdownMenuSubContext);
    const [rtl] = useRTL();
    const { ref } = useHotkeys({
        [rtl ? keys.RIGHT : keys.LEFT]: () => {
            subMenuInstance?.current?.close();
        },
    }, [subMenuInstance?.current], {
        disabled: flyout.status === "idle",
        ref: attributes?.ref,
    });
    const contentClassName = classNames(s.menu, className);
    return (_jsx(Popover.Content, { className: contentClassName, attributes: { ...attributes, ref }, children: children }));
};
const DropdownMenuSection = (props) => {
    const { children } = props;
    return (_jsx("div", { className: s.section, role: "group", children: children }));
};
const DropdownMenuItem = (props) => {
    const { onClick } = props;
    const { handleClose } = useFlyoutContext();
    const subTriggerContext = React.useContext(DropdownMenuSubTriggerContext);
    const handleClick = (e) => {
        /**
         * Stop event propagation to make sure outside click doesn't get triggered
         * after the content is closed
         */
        e.stopPropagation();
        /**
         * Don't close the menu when clicking on a trigger of a submenu
         */
        if (handleClose && !subTriggerContext) {
            handleClose({ closeParents: true, reason: "item-selection" });
        }
        if (onClick)
            onClick(e);
    };
    return (_jsx(MenuItem, { ...props, roundedCorners: true, className: [s.item, props.className], attributes: { role: "menuitem", ...props.attributes }, onClick: handleClick }));
};
const DropdownMenuSubMenu = (props) => {
    const { children } = props;
    const dropdownMenuRef = React.useRef(null);
    return (_jsx(DropdownMenuSubContext.Provider, { value: dropdownMenuRef, children: _jsx(DropdownMenu, { triggerType: "hover", position: "end-top", contentGap: 0.5, instanceRef: dropdownMenuRef, children: children }) }));
};
const DropdownMenuSubTriggerItem = (props) => {
    const { children, attributes, ...menuItemProps } = props;
    const subMenuInstance = React.useContext(DropdownMenuSubContext);
    const [rtl] = useRTL();
    const { ref } = useHotkeys({
        [rtl ? keys.LEFT : keys.RIGHT]: () => {
            subMenuInstance?.current?.open();
        },
    }, [], { ref: attributes?.ref, preventDefault: true });
    return (_jsx(DropdownMenuItem, { ...menuItemProps, attributes: { ...attributes, ref: ref }, endSlot: _jsx(Icon, { autoWidth: true, svg: IconChevronRight, className: s.arrow }), children: children }));
};
const DropdownMenuSubTrigger = (props) => {
    const { attributes, ...menuItemProps } = props;
    return (_jsx(DropdownMenuSubTriggerContext.Provider, { value: true, children: _jsx(DropdownMenu.Trigger, { children: (triggerAttributes) => {
                return (_jsx(DropdownMenuSubTriggerItem, { ...menuItemProps, attributes: {
                        ...attributes,
                        ...triggerAttributes,
                    } }));
            } }) }));
};
DropdownMenu.Dismissible = Popover.Dismissible;
DropdownMenu.Trigger = Popover.Trigger;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Section = DropdownMenuSection;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.SubMenu = DropdownMenuSubMenu;
DropdownMenu.SubTrigger = DropdownMenuSubTrigger;
DropdownMenu.displayName = "DropdownMenu";
DropdownMenuContent.displayName = "DropdownMenu.Content";
DropdownMenuSection.displayName = "DropdownMenu.Section";
DropdownMenuItem.displayName = "DropdownMenu.Item";
DropdownMenuSubMenu.displayName = "DropdownMenu.SubMenu";
DropdownMenuSubTrigger.displayName = "DropdownMenu.SubTrigger";
export default DropdownMenu;
